
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Download, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { get } from '@/services/apiService';

interface EventDetails {
  id: string;
  name: string;
  startDate?: string;
  location?: string;
}

interface EventStatistic {
  id: string;
  event_id: string;
  event_name: string;
  event_details?: EventDetails;
  total_views: number | null;
  unique_views: number | null;
  click_through_rate: number | null;
  conversion_rate: number | null;
  last_updated: string;
}

const EventStatisticsPanel = () => {
  const [statistics, setStatistics] = useState<EventStatistic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchEventDetails = async (eventIds: string[]): Promise<Map<string, EventDetails>> => {
    const eventDetailsMap = new Map<string, EventDetails>();
    
    try {
      // Fetch events from the legacy API /occasions endpoint
      const response = await get<{ 'hydra:member': any[] }>('/occasions');
      const events = response.data['hydra:member'] || [];
      
      console.log('Fetched events from API:', events);
      console.log('Looking for event IDs:', eventIds);
      
      // Create a map of event details by ID
      events.forEach(event => {
        // Extract ID from @id property (e.g., "/occasions/147389" -> "147389")
        const idFromAtId = event['@id']?.split('/').pop();
        const directId = event.id?.toString();
        
        console.log('Processing event:', { 
          '@id': event['@id'], 
          idFromAtId, 
          directId, 
          name: event.name 
        });
        
        // Check both @id extracted number and direct id property
        eventIds.forEach(searchId => {
          if (idFromAtId === searchId || directId === searchId) {
            console.log('Match found for event ID:', searchId, 'with event:', event.name);
            eventDetailsMap.set(searchId, {
              id: searchId,
              name: event.name || `Esemény ${searchId}`,
              startDate: event.startDate,
              location: event.location?.name || event.location
            });
          }
        });
      });
      
      console.log('Final event details map:', eventDetailsMap);
    } catch (error) {
      console.error('Error fetching event details:', error);
      // If API fails, create fallback entries
      eventIds.forEach(id => {
        eventDetailsMap.set(id, {
          id,
          name: `Esemény ${id}`,
        });
      });
    }
    
    return eventDetailsMap;
  };

  const loadStatistics = async () => {
    setIsLoading(true);
    try {
      // Fetch analytics events with session_id for unique tracking
      const { data, error } = await supabase
        .from('analytics_events')
        .select('event_id, event_type, session_id, user_id, created_at')
        .not('event_id', 'is', null)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading analytics events:', error);
        toast({
          title: "Hiba",
          description: "Nem sikerült betölteni az esemény statisztikákat",
          variant: "destructive"
        });
      } else {
        // Aggregate statistics by event_id
        const statsMap = new Map<string, Omit<EventStatistic, 'event_name' | 'event_details'>>();
        
        // Track unique sessions per event for unique views calculation
        const uniqueSessionsMap = new Map<string, Set<string>>();
        
        data?.forEach(event => {
          if (!event.event_id) return;
          
          const eventId = event.event_id;
          
          // Initialize stats if not exists
          if (!statsMap.has(eventId)) {
            statsMap.set(eventId, {
              id: eventId,
              event_id: eventId,
              total_views: 0,
              unique_views: 0,
              click_through_rate: 0,
              conversion_rate: 0,
              last_updated: event.created_at
            });
          }
          
          // Initialize unique sessions tracking
          if (!uniqueSessionsMap.has(eventId)) {
            uniqueSessionsMap.set(eventId, new Set<string>());
          }
          
          const stat = statsMap.get(eventId)!;
          
          // Count views (only event_view, not clicks)
          if (event.event_type === 'event_view') {
            stat.total_views = (stat.total_views || 0) + 1;
            // Track unique sessions for this event
            if (event.session_id) {
              uniqueSessionsMap.get(eventId)!.add(event.session_id);
            }
          }
          
          // Update last_updated to the most recent event
          if (event.created_at > stat.last_updated) {
            stat.last_updated = event.created_at;
          }
        });
        
        // Calculate unique views and click-through rates
        statsMap.forEach((stat, eventId) => {
          // Set unique views based on unique sessions
          stat.unique_views = uniqueSessionsMap.get(eventId)?.size || 0;
          
          // Calculate click-through rate: clicks / views * 100
          const clicks = data?.filter(e => e.event_id === eventId && e.event_type === 'event_click').length || 0;
          const views = stat.total_views || 0;
          
          if (views > 0) {
            stat.click_through_rate = (clicks / views) * 100;
          }
          
          // For now, we don't have a clear definition of conversion, so leave it at 0
          // Could be defined as event_detail_view or some other meaningful action
          stat.conversion_rate = 0;
        });

        // Fetch event details for all event IDs
        const eventIds = Array.from(statsMap.keys());
        const eventDetailsMap = await fetchEventDetails(eventIds);

        // Combine statistics with event details
        const statisticsWithDetails: EventStatistic[] = Array.from(statsMap.entries()).map(([eventId, stat]) => {
          const eventDetails = eventDetailsMap.get(eventId);
          return {
            ...stat,
            event_name: eventDetails?.name || `Esemény ${eventId}`,
            event_details: eventDetails
          };
        });
        
        setStatistics(statisticsWithDetails);
      }
    } catch (error) {
      console.error('Error loading event statistics:', error);
      toast({
        title: "Hiba", 
        description: "Nem sikerült betölteni az esemény statisztikákat",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadStatistics();
  }, []);

  const handleExportCSV = () => {
    if (statistics.length === 0) {
      toast({
        title: "Nincs adat",
        description: "Nincsenek exportálható statisztikák",
        variant: "destructive"
      });
      return;
    }

    const headers = ['Esemény neve', 'Esemény ID', 'Összes megtekintés', 'Egyedi megtekintések', 'Átkattintási arány (%)', 'Konverziós arány (%)', 'Utolsó frissítés'];
    const rows = statistics.map(stat => [
      stat.event_name,
      stat.event_id,
      (stat.total_views ?? 0).toString(),
      (stat.unique_views ?? 0).toString(),
      (stat.click_through_rate ?? 0).toString(),
      (stat.conversion_rate ?? 0).toString(),
      new Date(stat.last_updated).toLocaleDateString('hu-HU')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `esemeny-statisztikak-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Sikeres exportálás",
      description: "A statisztikák CSV fájlba exportálva"
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Esemény Statisztikák</CardTitle>
            <CardDescription>
              Valós idejű statisztikák az események megtekintéséről és interakciókról
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadStatistics}
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Frissítés
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              disabled={isLoading || statistics.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              CSV Export
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin mr-2" />
            <span>Statisztikák betöltése...</span>
          </div>
        ) : statistics.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>Még nincsenek esemény statisztikák.</p>
            <p className="text-sm mt-2">
              A statisztikák akkor jelennek meg, amikor felhasználók megtekintik az eseményeket.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Esemény neve</TableHead>
                <TableHead>ID</TableHead>
                <TableHead className="text-right">Összes megtekintés</TableHead>
                <TableHead className="text-right">Egyedi megtekintések</TableHead>
                <TableHead className="text-right">Átkattintási arány (%)</TableHead>
                <TableHead className="text-right">Konverziós arány (%)</TableHead>
                <TableHead>Utolsó frissítés</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {statistics.map((stat) => (
                <TableRow key={stat.id}>
                  <TableCell className="font-medium">{stat.event_name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{stat.event_id}</TableCell>
                  <TableCell className="text-right">{stat.total_views ?? 0}</TableCell>
                  <TableCell className="text-right">{stat.unique_views ?? 0}</TableCell>
                  <TableCell className="text-right">{(stat.click_through_rate ?? 0).toFixed(1)}%</TableCell>
                  <TableCell className="text-right">{(stat.conversion_rate ?? 0).toFixed(1)}%</TableCell>
                  <TableCell>{new Date(stat.last_updated).toLocaleDateString('hu-HU')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default EventStatisticsPanel;
